import type {
	ShadowUpdateFloorRequest,
	ShadowUpdateZoneRequest,
	ZoneBoundary as UiZoneBoundary,
	UpdateSensorRequest,
} from "@sitescene/types";

import { normalizeGeoJsonPolygon } from "@sitescene/shared";

import type { UpdateLocationRequest } from "@/api/locations";

import type {
	ShadowFloor,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";

import { getRefId } from "./installation-ids";

/**
 * Map UI sensor patch → PUT /shadow/sensors/{id} body.
 */
export const toShadowSensorUpdate = (
	sensorData: Partial<ShadowSensor>,
): UpdateSensorRequest => {
	const body: UpdateSensorRequest = {};

	if (sensorData.name !== undefined) body.name = sensorData.name;
	if (sensorData.status !== undefined) body.status = sensorData.status;
	if (sensorData.type !== undefined) body.type = sensorData.type;
	if (sensorData.zone !== undefined) {
		const zoneId = getRefId(sensorData.zone);
		if (zoneId) body.zone = zoneId;
	}
	if (sensorData.geolocation !== undefined) {
		body.geolocation =
			sensorData.geolocation === null
				? null
				: {
						coordinates: sensorData.geolocation.coordinates,
						type: "Point",
					};
	}

	return body;
};

/**
 * Map UI zone patch → PATCH /shadow/zones/{id} body.
 */
export const toShadowZoneUpdate = (
	zoneData: Partial<Omit<ShadowZone, "floor">> & {
		floor?: null | ShadowZone["floor"];
	},
): ShadowUpdateZoneRequest => {
	const body: ShadowUpdateZoneRequest = {};

	if (zoneData.name !== undefined) body.name = zoneData.name;
	if (zoneData.description !== undefined) {
		body.description = zoneData.description;
	}
	if (zoneData.borderWidth !== undefined) {
		body.borderWidth = zoneData.borderWidth;
	}
	if (zoneData.boundaryKind !== undefined) {
		body.boundaryKind = zoneData.boundaryKind;
	}
	if (zoneData.boundaryMode !== undefined) {
		body.boundaryMode = zoneData.boundaryMode;
	}
	if (zoneData.color !== undefined) body.color = zoneData.color;
	if (zoneData.floor !== undefined) {
		body.floor =
			zoneData.floor === null ? null : (getRefId(zoneData.floor) ?? null);
	}
	if (zoneData.sortOrder !== undefined) body.sortOrder = zoneData.sortOrder;
	if (zoneData.boundary !== undefined) {
		body.boundary = zoneData.boundary as null | UiZoneBoundary as
			| null
			| ShadowUpdateZoneRequest["boundary"];
	}

	return body;
};

/**
 * Map UI floor patch → PATCH /shadow/floors/{id} body.
 *
 * `image: null` clears the stored key and corners together. A partial image
 * is sent whole, because the API replaces the image subdocument.
 * `boundary: null` clears a drawn outline.
 */
export const toShadowFloorUpdate = (
	floorData: Partial<Omit<ShadowFloor, "boundary" | "description">> & {
		boundary?: null | ShadowFloor["boundary"];
		description?: null | string;
	},
): ShadowUpdateFloorRequest => {
	const body: ShadowUpdateFloorRequest = {};

	if (floorData.name !== undefined) body.name = floorData.name;
	if (floorData.description !== undefined) {
		body.description = floorData.description;
	}
	if (floorData.sortOrder !== undefined) body.sortOrder = floorData.sortOrder;
	if (floorData.outlineMode !== undefined) {
		body.outlineMode = floorData.outlineMode;
	}
	if (floorData.boundary !== undefined) {
		// The API only accepts valid GeoJSON polygons. Skip a shape we cannot
		// repair so the rest of the update still saves.
		if (floorData.boundary === null) {
			body.boundary = null;
		} else {
			const polygon = normalizeGeoJsonPolygon(floorData.boundary);
			if (polygon) body.boundary = polygon;
		}
	}
	if (floorData.borderWidth !== undefined) {
		body.borderWidth = floorData.borderWidth;
	}
	if (floorData.color !== undefined) body.color = floorData.color;
	if (floorData.fill !== undefined) body.fill = floorData.fill;
	if (floorData.image !== undefined) {
		body.image =
			floorData.image === null
				? null
				: {
						corners: floorData.image.corners,
						height: floorData.image.height,
						key: floorData.image.key,
						...(floorData.image.opacity !== undefined
							? { opacity: floorData.image.opacity }
							: {}),
						width: floorData.image.width,
					};
	}

	return body;
};

/**
 * Map UI location fields → PATCH /shadow/locations/{id} body.
 */
export const toShadowLocationUpdate = (updates: {
	address?: UpdateLocationRequest["address"];
	coordinates?: null | UpdateLocationRequest["coordinates"];
	name?: string;
	timezone?: string;
}): UpdateLocationRequest => {
	const body: UpdateLocationRequest = {};
	if (updates.name !== undefined) body.name = updates.name;
	if (updates.timezone !== undefined) body.timezone = updates.timezone;
	if (updates.address !== undefined) body.address = updates.address;
	if (updates.coordinates !== undefined && updates.coordinates !== null) {
		body.coordinates = updates.coordinates;
	}
	return body;
};
